import { IPoint } from "../../interfaces/IPoint";
import { IRect } from "../../interfaces/IRect";
import { RectUtil } from "../../utils/RectUtil";
import { DrawUtil } from "../../utils/DrawUtil";
import { store } from "../..";
import { ImageData, LabelRect } from "../../store/labels/types";
import {
  updateActiveLabelId,
  updateActiveLabelNameId,
  updateFirstLabelCreatedFlag,
  updateHighlightedLabelId,
  updateImageDataById,
  updateImageDataByIdwithoutundo,
  updatePendingTrackId,
} from "../../store/labels/actionCreators";
import { PointUtil } from "../../utils/PointUtil";
import { RectAnchor } from "../../data/RectAnchor";
import { RenderEngineSettings } from "../../settings/RenderEngineSettings";
import { updateCustomCursorStyle, updateInlineLabelPicker } from "../../store/general/actionCreators";
import { CustomCursorStyle } from "../../data/enums/CustomCursorStyle";
import { LabelsSelector } from "../../store/selectors/LabelsSelector";
import { EditorData } from "../../data/EditorData";
import { BaseRenderEngine } from "./BaseRenderEngine";
import { RenderEngineUtil } from "../../utils/RenderEngineUtil";
import { LabelType } from "../../data/enums/LabelType";
import { EditorActions } from "../actions/EditorActions";
import { GeneralSelector } from "../../store/selectors/GeneralSelector";
import { LabelStatus } from "../../data/enums/LabelStatus";
import { LabelUtil } from "../../utils/LabelUtil";
import { TrackActions } from "../actions/TrackActions";
export class RectRenderEngine extends BaseRenderEngine {
  // =================================================================================================================
  // STATE
  // =================================================================================================================

  private startCreateRectPoint: IPoint;
  private startResizeRectAnchor: RectAnchor;
  private isDragging = false;
  private dragPosition: IPoint = { x: 0, y: 0 };
  private selectedOnMouseDown = false;
  private hoveredRect: LabelRect | null = null;
  private hoveredAnchor: RectAnchor | null = null;
  private hoveredImageId: string | null = null;
  private pendingDragData: EditorData | null = null;
  private pendingDragFrame: number | null = null;
  private staticRectsLayer: HTMLCanvasElement | null = null;
  private staticRectsImage: ImageData | null = null;
  private staticRectsLayoutKey = "";

  public constructor(canvas: HTMLCanvasElement) {
    super(canvas);
    this.labelType = LabelType.RECT;
  }
  // =================================================================================================================
  // EVENT HANDLERS
  // =================================================================================================================

  public mouseDownHandler = (data: EditorData) => {
    const event = data.event as MouseEvent | undefined;
    if (event?.button !== 0) {
      return;
    }

    this.selectedOnMouseDown = false;
    // A second click completes the box even if its endpoint is on another box.
    if (this.startCreateRectPoint) return;

    if (RenderEngineUtil.isMouseOverImage(data)) {
      const rectUnderMouse: LabelRect = this.getRectUnderMouse(data);
      if (!!rectUnderMouse) {
        this.selectedOnMouseDown = true;
        store.dispatch(updateActiveLabelId(rectUnderMouse.id));
       
        const rect: IRect = this.calculateRectRelativeToActiveImage(
          rectUnderMouse.rect,
          data
        );
        const anchorUnderMouse: RectAnchor = this.getAnchorUnderMouseByRect(
          rect,
          data.mousePositionOnViewPortContent,
          data.viewPortContentImageRect
        );
        if (
          !!anchorUnderMouse &&
          rectUnderMouse.status === LabelStatus.ACCEPTED
        ) {
          store.dispatch(updateActiveLabelId(rectUnderMouse.id));
          this.startRectResize(anchorUnderMouse);
          // console.log("on rect line")
        } else if (this.isMouseOverRectEdges(rectUnderMouse.rect, data)) {
          this.isDragging = true;
          this.dragPosition = RectUtil.snapPointToRect(
            data.mousePositionOnViewPortContent,
            data.viewPortContentImageRect
          );
        }
      }
    }
  };
  public updateMousePosition = ev => {
       return ev.clientX;
  };

  public mouseUpHandler = (data: EditorData) => {
    const ev = data.event as MouseEvent | undefined;
    const isCanvasTarget = (ev?.target as HTMLElement)?.tagName === "CANVAS";
    const isPrimaryButton = ev?.button === 0;
    const isSecondaryButton = ev?.button === 2;
    const wasSelecting = this.selectedOnMouseDown;
    this.selectedOnMouseDown = false;

    if (this.isDragging) this.flushPendingDrag(data);
    this.isDragging = false;

    if (!isCanvasTarget || (!isPrimaryButton && !isSecondaryButton)) {
      this.endRectTransformation();
      return;
    }

    if (this.startCreateRectPoint) {
      const mousePositionSnapped: IPoint = RectUtil.snapPointToRect(
        data.mousePositionOnViewPortContent,
        data.viewPortContentImageRect
      );
      const endOnImage: IPoint = RenderEngineUtil.transferPointFromViewPortContentToImage(
        mousePositionSnapped,
        data
      );
      if (!PointUtil.equals(this.startCreateRectPoint, endOnImage)) {
        const minX = Math.min(this.startCreateRectPoint.x, endOnImage.x);
        const minY = Math.min(this.startCreateRectPoint.y, endOnImage.y);
        const maxX = Math.max(this.startCreateRectPoint.x, endOnImage.x);
        const maxY = Math.max(this.startCreateRectPoint.y, endOnImage.y);
        const rect = { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
        const shouldOpenInlinePicker =
          isSecondaryButton || !LabelsSelector.getActiveLabelNameId();
        const created = this.addRectLabel(rect);
        if (created && shouldOpenInlinePicker) {
          window.requestAnimationFrame(() => {
            store.dispatch(updateInlineLabelPicker({
              open: true,
              clientX: ev?.clientX ?? null,
              clientY: ev?.clientY ?? null,
              shapeId: created.id,
              shapeType: "rect",
            }));
          });
        }
      }
      this.endRectTransformation();
      return;
    }

    if (this.startResizeRectAnchor) {
      const scale: number = RenderEngineUtil.calculateImageScale(data);
      const activeLabelRect: LabelRect = LabelsSelector.getActiveRectLabel();
      const mousePositionSnapped: IPoint = RectUtil.snapPointToRect(
        data.mousePositionOnViewPortContent,
        data.viewPortContentImageRect
      );
      if (activeLabelRect) {
        const rect: IRect = this.calculateRectRelativeToActiveImage(activeLabelRect.rect, data);
        const startAnchorPosition: IPoint = PointUtil.add(
          this.startResizeRectAnchor.position, data.viewPortContentImageRect
        );
        const delta: IPoint = PointUtil.subtract(mousePositionSnapped, startAnchorPosition);
        const resizeRect: IRect = RectUtil.resizeRect(
          rect, this.startResizeRectAnchor.type, delta
        );
        if (resizeRect.x < 0) resizeRect.x = 0;
        if (resizeRect.y < 0) resizeRect.y = 0;
        if (resizeRect.x + resizeRect.width > data.viewPortContentImageRect.width)
          resizeRect.width = data.viewPortContentImageRect.width - resizeRect.x;
        if (resizeRect.y + resizeRect.height > data.viewPortContentImageRect.height)
          resizeRect.height = data.viewPortContentImageRect.height - resizeRect.y;
        const scaledRect: IRect = RectUtil.scaleRect(resizeRect, scale);
        const imageData = LabelsSelector.getActiveImageData();
        store.dispatch(updateImageDataById(imageData.image_id, {
          ...imageData,
          labelRects: imageData.labelRects.map((labelRect: LabelRect) =>
            labelRect.id === activeLabelRect.id
              ? { ...labelRect, rect: scaledRect }
              : labelRect
          ),
        }));
      }
      this.endRectTransformation();
      return;
    }

    if (wasSelecting || !isPrimaryButton || !data.mousePositionOnViewPortContent) return;

    const dx = data.mousePositionOnViewPortContent.x - data.absoluteViewPortContentScrollPosition.x;
    const dy = data.mousePositionOnViewPortContent.y - data.absoluteViewPortContentScrollPosition.y;
    if (
      dx + 4 < data.viewPortSize.width &&
      dx > 0 &&
      dy + 4 < data.viewPortSize.height &&
      dy > 0
    ) {
      this.startRectCreation(data);
    }
  };




  public mouseMoveHandler = (data: EditorData) => {
    const isOverImage: boolean = RenderEngineUtil.resizeChecker(data);
    
    // if (
    //   !!data.viewPortContentImageRect &&
    //   !!data.mousePositionOnViewPortContent
    // ) {
      

      if (isOverImage ) {
        if(!this.startResizeRectAnchor){
          const labelRect: LabelRect = this.resolveHoveredRect(data);
          if (!!labelRect && !this.isInProgress()) {
            if (LabelsSelector.getHighlightedLabelId() !== labelRect.id) {
              store.dispatch(updateHighlightedLabelId(labelRect.id));
            }
          } else {
            if (LabelsSelector.getHighlightedLabelId() !== null) {
              store.dispatch(updateHighlightedLabelId(null));
            }
          }
         
        }
      
        
      } else {
        this.hoveredRect = null;
        this.hoveredAnchor = null;
        this.hoveredImageId = null;
      }


    // }
   
    if (this.isDragging) {
      this.scheduleDragUpdate(data);
    }
  };

  private scheduleDragUpdate(data: EditorData): void {
    this.pendingDragData = data;
    if (this.pendingDragFrame !== null) return;

    this.pendingDragFrame = window.requestAnimationFrame(() => {
      this.pendingDragFrame = null;
      const latestData = this.pendingDragData;
      this.pendingDragData = null;
      if (latestData) this.applyDragUpdate(latestData);
    });
  }

  private flushPendingDrag(data?: EditorData): void {
    if (this.pendingDragFrame !== null) {
      window.cancelAnimationFrame(this.pendingDragFrame);
      this.pendingDragFrame = null;
    }
    const latestData = data || this.pendingDragData;
    this.pendingDragData = null;
    if (latestData && this.isDragging) this.applyDragUpdate(latestData);
  }

  private applyDragUpdate(data: EditorData): void {
    const activeLabelRect = LabelsSelector.getActiveRectLabel();
    if (!activeLabelRect?.rect || !this.isDragging || !data.mousePositionOnViewPortContent) return;
    if (data.mousePositionOnViewPortContent.x <= 0 || data.mousePositionOnViewPortContent.y <= 0) return;

    const rect = this.calculateRectRelativeToActiveImage(activeLabelRect.rect, data);
    const mousePositionSnapped = RectUtil.snapPointToRect(
      data.mousePositionOnViewPortContent,
      data.viewPortContentImageRect,
    );
    if (PointUtil.equals(this.dragPosition, mousePositionSnapped)) return;
    rect.x -= this.dragPosition.x - mousePositionSnapped.x;
    rect.y -= this.dragPosition.y - mousePositionSnapped.y;
    rect.x = Math.max(0, Math.min(rect.x, data.viewPortContentImageRect.width - rect.width));
    rect.y = Math.max(0, Math.min(rect.y, data.viewPortContentImageRect.height - rect.height));
    this.dragPosition = mousePositionSnapped;

    const imageData = LabelsSelector.getActiveImageData();
    const scaledRect = RectUtil.scaleRect(rect, RenderEngineUtil.calculateImageScale(data));
    store.dispatch(updateImageDataByIdwithoutundo(imageData.image_id, {
      ...imageData,
      labelRects: imageData.labelRects.map((labelRect: LabelRect) =>
        labelRect.id === activeLabelRect.id ? { ...labelRect, rect: scaledRect } : labelRect,
      ),
    }));
  }
  public pasteHandler(): void {
    const copiedLabels = this.getCopiedLabels<LabelRect>();
    if (copiedLabels.length === 0) return;
    copiedLabels.forEach((rectLabel) => {
      store.dispatch(updateActiveLabelNameId(rectLabel.labelId));
      this.addRectLabel(rectLabel.rect);
    });
    EditorActions.fullRender();
  }


  // =================================================================================================================
  // RENDERING
  // =================================================================================================================

  public render(data: EditorData) {
    const activeLabelId: string = LabelsSelector.getActiveLabelId();
    const imageData: ImageData = LabelsSelector.getActiveImageData();
    if (imageData) {
      const highlightedLabelId: string = LabelsSelector.getHighlightedLabelId();
      const opacityValue = localStorage.getItem("opacityValue");
      const opacity = opacityValue ? parseFloat(opacityValue) : 0.2;
      this.drawStaticRects(imageData, data, activeLabelId, highlightedLabelId, opacity);

      const activeRect = imageData.labelRects.find(
        (labelRect) => String(labelRect.id) === String(activeLabelId),
      );
      const highlightedRect = imageData.labelRects.find(
        (labelRect) =>
          labelRect.status === LabelStatus.ACCEPTED &&
          labelRect.id === highlightedLabelId,
      );
      if (activeRect && activeRect.show !== false) this.drawActiveRect(activeRect, data);
      if (
        highlightedRect && highlightedRect.show !== false &&
        String(highlightedRect.id) !== String(activeLabelId)
      ) {
        this.drawInactiveRect(highlightedRect, data);
      }
      this.drawCurrentlyCreatedRect(data);
      this.updateCursorStyle(data);
    }
  }

  private drawStaticRects(
    imageData: ImageData,
    data: EditorData,
    activeLabelId: string,
    highlightedLabelId: string,
    opacity: number,
  ): void {
    const imageRect = data.viewPortContentImageRect;
    const layoutKey = [
      this.canvas.width,
      this.canvas.height,
      imageRect.x,
      imageRect.y,
      imageRect.width,
      imageRect.height,
      activeLabelId || "",
      highlightedLabelId || "",
      opacity,
    ].join(":");
    const isCacheValid =
      this.staticRectsLayer &&
      this.staticRectsImage === imageData &&
      this.staticRectsLayoutKey === layoutKey;

    if (!isCacheValid) {
      const layer = this.staticRectsLayer || document.createElement("canvas");
      layer.width = this.canvas.width;
      layer.height = this.canvas.height;
      DrawUtil.clearCanvas(layer);
      const inactiveRects = imageData.labelRects.reduce<Array<{
        rect: IRect;
        lineColor: string;
        fillColor: string;
        thickness: number;
      }>>((rectangles, labelRect) => {
        const isActive = String(labelRect.id) === String(activeLabelId);
        const isHighlighted =
          labelRect.status === LabelStatus.ACCEPTED &&
          labelRect.id === highlightedLabelId;
        if (labelRect.show !== false && !isActive && !isHighlighted) {
          const lineColor = BaseRenderEngine.resolveLabelLineColor(labelRect.labelId, false);
          rectangles.push({
            rect: RenderEngineUtil.setRectBetweenPixels(
              RenderEngineUtil.transferRectFromImageToViewPortContent(labelRect.rect, data),
            ),
            lineColor,
            fillColor: DrawUtil.hexToRGB(lineColor, opacity),
            thickness: RenderEngineSettings.LINE_THICKNESS,
          });
        }
        return rectangles;
      }, []);
      DrawUtil.drawRects(layer, inactiveRects);
      this.staticRectsLayer = layer;
      this.staticRectsImage = imageData;
      this.staticRectsLayoutKey = layoutKey;
    }

    const context = this.canvas.getContext("2d");
    if (context && this.staticRectsLayer) context.drawImage(this.staticRectsLayer, 0, 0);
  }

  private drawCurrentlyCreatedRect(data: EditorData) {
    if (!!this.startCreateRectPoint) {
      const startOnCanvas: IPoint =
        RenderEngineUtil.transferPointFromImageToViewPortContent(
          this.startCreateRectPoint,
          data
        );
      const mousePositionSnapped: IPoint = RectUtil.snapPointToRect(
        data.mousePositionOnViewPortContent,
        data.viewPortContentImageRect
      );
      const activeRect: IRect = {
        x: startOnCanvas.x,
        y: startOnCanvas.y,
        width: mousePositionSnapped.x - startOnCanvas.x,
        height: mousePositionSnapped.y - startOnCanvas.y,
      };
      const activeRectBetweenPixels =
        RenderEngineUtil.setRectBetweenPixels(activeRect);
      const lineColor: string = BaseRenderEngine.resolveLabelLineColor(
        null,
        true
      );
      DrawUtil.drawRect(
        this.canvas,
        "select-label",
        activeRectBetweenPixels,
        lineColor,
        RenderEngineSettings.LINE_THICKNESS
      );
    }
  }

  private drawInactiveRect(labelRect: LabelRect, data: EditorData) {
    const rectOnImage: IRect =
      RenderEngineUtil.transferRectFromImageToViewPortContent(
        labelRect.rect,
        data
      );
    const highlightedLabelId: string = LabelsSelector.getHighlightedLabelId();
    const LabelName: string = LabelsSelector.isVideoTrackingEnabled() && labelRect.trackId
      ? TrackActions.displayName(labelRect.trackId)
      : BaseRenderEngine.resolveLabelLineName(labelRect.labelId);
    const displayAsActive: boolean =
      labelRect.status === LabelStatus.ACCEPTED &&
      labelRect.id === highlightedLabelId;
    const lineColor: string = BaseRenderEngine.resolveLabelLineColor(
      labelRect.labelId,
      displayAsActive
    );
    const anchorColor: string =
      BaseRenderEngine.resolveLabelAnchorColor(displayAsActive);
    this.renderRect(
      rectOnImage,
      LabelName,
      displayAsActive,
      lineColor,
      anchorColor
    );
  }
  private hide() {

  }
  private drawActiveRect(labelRect: LabelRect, data: EditorData) {
    let rect: IRect = this.calculateRectRelativeToActiveImage(
      labelRect.rect,
      data
    );
    if (!!this.startResizeRectAnchor ) {
      const startAnchorPosition: IPoint = PointUtil.add(
        this.startResizeRectAnchor.position,
        data.viewPortContentImageRect
      );
      const endAnchorPositionSnapped: IPoint = RectUtil.snapPointToRect(
        data.mousePositionOnViewPortContent,
        data.viewPortContentImageRect
      );
      const delta = PointUtil.subtract(
        endAnchorPositionSnapped,
        startAnchorPosition
      );
      rect = RectUtil.resizeRect(rect, this.startResizeRectAnchor.type, delta);

      if (rect.x < 0) rect.x = 0;
  if (rect.y < 0) rect.y = 0;
  if (rect.x + rect.width > data.viewPortContentImageRect.width)
    rect.width = data.viewPortContentImageRect.width - rect.x;
  if (rect.y + rect.height > data.viewPortContentImageRect.height)
    rect.height = data.viewPortContentImageRect.height - rect.y;
    }
    const rectOnImage: IRect = RectUtil.translate(
      rect,
      data.viewPortContentImageRect
    );
    const LabelName: string = LabelsSelector.isVideoTrackingEnabled() && labelRect.trackId
      ? TrackActions.displayName(labelRect.trackId)
      : BaseRenderEngine.resolveLabelLineName(labelRect.labelId);
    const lineColor: string = BaseRenderEngine.resolveLabelLineColor(
      labelRect.labelId,
      true
    );
    const anchorColor: string = BaseRenderEngine.resolveLabelAnchorColor(true);
    this.renderRectWithText(rectOnImage, LabelName, true, lineColor, anchorColor);
  }

  private renderRect(
    rectOnImage: IRect,
    LabelName: string,
    isActive: boolean,
    lineColor: string,
    anchorColor: string
  ) {
    const opacityValue = localStorage.getItem('opacityValue');
            const opacity = opacityValue ? parseFloat(opacityValue) : 0.2;
    const rectBetweenPixels =
      RenderEngineUtil.setRectBetweenPixels(rectOnImage);
    DrawUtil.drawRectWithFill(
      this.canvas,
      rectBetweenPixels,
      DrawUtil.hexToRGB(lineColor, opacity)
    );
    DrawUtil.drawRect(
      this.canvas,
      LabelName,
      rectBetweenPixels,
      lineColor,
      RenderEngineSettings.LINE_THICKNESS
    );
    if (isActive) {
      const handleCenters: IPoint[] = RectUtil.mapRectToAnchors(
        rectOnImage
      ).map((rectAnchor: RectAnchor) => rectAnchor.position);
      handleCenters.forEach((center: IPoint) => {
        const handleRect: IRect = RectUtil.getRectWithCenterAndSize(
          center,
          RenderEngineSettings.anchorSize
        );
        const handleRectBetweenPixels: IRect =
          RenderEngineUtil.setRectBetweenPixels(handleRect);
        DrawUtil.drawRectWithFill(
          this.canvas,
          handleRectBetweenPixels,
          anchorColor
        );
      });
    }
  }
  private renderRectWithText(
    rectOnImage: IRect,
    LabelName: string,
    isActive: boolean,
    lineColor: string,
    anchorColor: string
  ) {
    const opacityValue = localStorage.getItem('opacityValue');
    const opacity = opacityValue ? parseFloat(opacityValue) : 0.2;
    const rectBetweenPixels =
      RenderEngineUtil.setRectBetweenPixels(rectOnImage);
    DrawUtil.drawRectWithFill(
      this.canvas,
      rectBetweenPixels,
      DrawUtil.hexToRGB(lineColor, opacity)
    );
    DrawUtil.drawRectWithText(
      this.canvas,
      LabelName,
      rectBetweenPixels,
      lineColor,
      RenderEngineSettings.LINE_THICKNESS
    );
    if (isActive) {
      const handleCenters: IPoint[] = RectUtil.mapRectToAnchors(
        rectOnImage
      ).map((rectAnchor: RectAnchor) => rectAnchor.position);
      handleCenters.forEach((center: IPoint) => {
        const handleRect: IRect = RectUtil.getRectWithCenterAndSize(
          center,
          RenderEngineSettings.anchorSize
        );
        const handleRectBetweenPixels: IRect =
          RenderEngineUtil.setRectBetweenPixels(handleRect);
        DrawUtil.drawRectWithFill(
          this.canvas,
          handleRectBetweenPixels,
          anchorColor
        );
      });
    }
  }

  private updateCursorStyle(data: EditorData) {
    if (
      !!this.canvas &&
      !!data.mousePositionOnViewPortContent &&
      !GeneralSelector.getImageDragModeStatus()
    ) {
      const activeImageId = LabelsSelector.getActiveImageData()?.image_id;
      const rectUnderMouse =
        this.hoveredImageId === activeImageId
          ? this.hoveredRect
          : this.resolveHoveredRect(data);
      const rectAnchorUnderMouse = this.hoveredAnchor;
      if (
        (!!rectAnchorUnderMouse &&
          rectUnderMouse &&
          rectUnderMouse.status === LabelStatus.ACCEPTED) ||
        !!this.startResizeRectAnchor
      ) {
        this.setCursorStyle(CustomCursorStyle.RESIZE);
        return;
      } else if (rectUnderMouse && this.isMouseOverRectEdges(rectUnderMouse.rect, data)) {
        this.setCursorStyle(CustomCursorStyle.MOVE);
    } else if (RenderEngineUtil.isMouseOverCanvas(data)) {
        if (
          !RenderEngineUtil.isMouseOverImage(data) &&
          !!this.startCreateRectPoint
        )
          this.setCursorStyle(CustomCursorStyle.MOVE);
        else this.setCursorStyle(
          RenderEngineUtil.isMouseOverImage(data)
            ? CustomCursorStyle.DEFAULT
            : CustomCursorStyle.CANCEL,
        );
        this.canvas.style.cursor = "none";
      } else {
        this.canvas.style.cursor = "default";
      }
    }
  }

  private resolveHoveredRect(data: EditorData): LabelRect | null {
    const rect = this.getRectUnderMouse(data);
    this.hoveredRect = rect;
    this.hoveredImageId = LabelsSelector.getActiveImageData()?.image_id || null;
    this.hoveredAnchor = rect
      ? this.getAnchorUnderMouse(data, [rect])
      : null;
    return rect;
  }

  private setCursorStyle(style: CustomCursorStyle): void {
    if (GeneralSelector.getCustomCursorStyle() !== style) {
      store.dispatch(updateCustomCursorStyle(style));
    }
  }

  // =================================================================================================================
  // HELPERS
  // =================================================================================================================

  public isInProgress(): boolean {
    return !!this.startCreateRectPoint || !!this.startResizeRectAnchor || this.isDragging;
  }

  private calculateRectRelativeToActiveImage(
    rect: IRect,
    data: EditorData
  ): IRect {
    const scale: number = RenderEngineUtil.calculateImageScale(data);
    return RectUtil.scaleRect(rect, 1 / scale);
  }

  private addRectLabel = (rect: IRect) => {
    const activeLabelId = LabelsSelector.getActiveLabelNameId();
    const imageData: ImageData = LabelsSelector.getActiveImageData();
    const trackingEnabled = LabelsSelector.isVideoTrackingEnabled();
    const pendingTrackId = LabelsSelector.getPendingTrackId();

    // With tracking disabled, a rectangle belongs only to the current frame.
    if (!trackingEnabled) {
      const labelRect: LabelRect = LabelUtil.createLabelRect(activeLabelId, rect);
      store.dispatch(updateImageDataById(imageData.image_id, {
        ...imageData,
        labelRects: [...imageData.labelRects, labelRect],
      }));
      store.dispatch(updateFirstLabelCreatedFlag(true));
      store.dispatch(updateActiveLabelId(labelRect.id));
      return labelRect;
    }

    const reusingTrack = Boolean(pendingTrackId);
    const trackId = pendingTrackId || TrackActions.takeNextTrackId();
    if (!trackId) {
      window.alert("Track IDs are still being prepared. Try again in a moment.");
      return null;
    }
    const existingTrackRect = reusingTrack
      ? LabelsSelector.getImagesData().flatMap((image) => image.labelRects || [])
          .find((existing) => existing.trackId === trackId)
      : null;
    const labelRect: LabelRect = LabelUtil.createLabelRect(
      existingTrackRect?.labelId ?? activeLabelId,
      rect,
      trackId
    );

    const placedRect = TrackActions.placeTrackRect(labelRect);
    if (!placedRect) return null;

    // Continuing a track is a one-draw action. The next box starts a new object.
    if (reusingTrack) store.dispatch(updatePendingTrackId(null));

    store.dispatch(updateFirstLabelCreatedFlag(true));
    store.dispatch(updateActiveLabelId(placedRect.id));
    return placedRect;
  };

  private getRectUnderMouse(data: EditorData): LabelRect {
    const activeRectLabel: LabelRect = LabelsSelector.getActiveRectLabel();
    if (!!activeRectLabel && activeRectLabel.show !== false && this.isMouseOverRect(activeRectLabel.rect, data)) {
      return activeRectLabel;
    }

    const labelRects: LabelRect[] = LabelsSelector.getActiveImageData().labelRects;

    for (let i = 0; i < labelRects.length; i++) {
      if (labelRects[i].show !== false && this.isMouseOverRect(labelRects[i].rect, data)) {
        return labelRects[i];
      }
    }
    return null;
  }

  private isMouseOverRect(rect: IRect, data: EditorData): boolean {
    const rectOnCanvas = RectUtil.translate(
      this.calculateRectRelativeToActiveImage(rect, data),
      data.viewPortContentImageRect
    );
    return RectUtil.isPointInside(rectOnCanvas, data.mousePositionOnViewPortContent) ||
      this.isMouseOverRectEdges(rect, data);
  }

  private isMouseOverRectEdges(rect: IRect, data: EditorData): boolean {
    const rectOnImage: IRect = RectUtil.translate(
      this.calculateRectRelativeToActiveImage(rect, data),
      data.viewPortContentImageRect
    );

    const outerRectDelta: IPoint = {
      x: RenderEngineSettings.anchorHoverSize.width / 2,
      y: RenderEngineSettings.anchorHoverSize.height / 2,
    };
    const outerRect: IRect = RectUtil.expand(rectOnImage, outerRectDelta);

    const innerRectDelta: IPoint = {
      x: -RenderEngineSettings.anchorHoverSize.width / 2,
      y: -RenderEngineSettings.anchorHoverSize.height / 2,
    };
    const innerRect: IRect = RectUtil.expand(rectOnImage, innerRectDelta);

    return (
      RectUtil.isPointInside(outerRect, data.mousePositionOnViewPortContent) &&
      !RectUtil.isPointInside(innerRect, data.mousePositionOnViewPortContent)
    );
  }

  private getAnchorUnderMouseByRect(
    rect: IRect,
    mousePosition: IPoint,
    imageRect: IRect
  ): RectAnchor {
    const rectAnchors: RectAnchor[] = RectUtil.mapRectToAnchors(rect);
    for (let i = 0; i < rectAnchors.length; i++) {
      const anchorRect: IRect = RectUtil.translate(
        RectUtil.getRectWithCenterAndSize(
          rectAnchors[i].position,
          RenderEngineSettings.anchorHoverSize
        ),
        imageRect
      );
      if (
        !!mousePosition &&
        RectUtil.isPointInside(anchorRect, mousePosition)
      ) {
        return rectAnchors[i];
      }
    }
    return null;
  }

  private getAnchorUnderMouse(
    data: EditorData,
    labelRects: LabelRect[] = LabelsSelector.getActiveImageData().labelRects
  ): RectAnchor {
    for (let i = 0; i < labelRects.length; i++) {
      const rect: IRect = this.calculateRectRelativeToActiveImage(
        labelRects[i].rect,
        data
      );
      const rectAnchor = this.getAnchorUnderMouseByRect(
        rect,
        data.mousePositionOnViewPortContent,
        data.viewPortContentImageRect
      );
      if (!!rectAnchor) return rectAnchor;
    }
    return null;
  }

  private startRectCreation(data: EditorData) {
    const mousePositionSnapped: IPoint = RectUtil.snapPointToRect(
      data.mousePositionOnViewPortContent,
      data.viewPortContentImageRect
    );
    this.startCreateRectPoint =
      RenderEngineUtil.transferPointFromViewPortContentToImage(
        mousePositionSnapped,
        data
      );
    store.dispatch(updateActiveLabelId(null));
  }
  public cancelLabelCreation() {
    this.dispose();
    store.dispatch(updateActiveLabelId(null));
  }
  public dispose(): void {
    if (this.pendingDragFrame !== null) {
      window.cancelAnimationFrame(this.pendingDragFrame);
      this.pendingDragFrame = null;
    }
    this.pendingDragData = null;
    this.isDragging = false;
    this.selectedOnMouseDown = false;
    this.endRectTransformation();
  }
  public startRectPositioning(data: EditorData) {
      const mousePositionSnapped: IPoint = RectUtil.snapPointToRect(
        data.mousePositionOnViewPortContent,
        data.viewPortContentImageRect
      );
      this.startCreateRectPoint =
        RenderEngineUtil.transferPointFromViewPortContentToImage(
          mousePositionSnapped,
          data
        );
      store.dispatch(updateActiveLabelId(null));
    }
  private startRectResize(activatedAnchor: RectAnchor) {
    this.startResizeRectAnchor = activatedAnchor;
  }

  private endRectTransformation() {
    this.startCreateRectPoint = null;
    this.startResizeRectAnchor = null;
  }
}
